import type { SortingStrategy } from "@dnd-kit/sortable";
import type { LayoutRect } from "@dnd-kit/core";
import type { Transform } from "@dnd-kit/utilities";

const defaultScale = {
  scaleX: 1,
  scaleY: 1,
};

function getItemGap(
  layoutRects: LayoutRect[],
  index: number,
  activeIndex: number
): number {
  const current = layoutRects[index];
  const prev = layoutRects[index - 1];
  const next = layoutRects[index + 1];

  if (!current) return 0;

  if (!prev && !next) return 0;

  if (activeIndex < index) {
    if (prev) {
      return current.offsetLeft - (prev.offsetLeft + prev.width);
    } else if (next) {
      return next.offsetLeft - (current.offsetLeft + current.width);
    } else {
      return 0;
    }
  }

  if (next) {
    return next.offsetLeft - (current.offsetLeft + current.width);
  } else if (prev) {
    return current.offsetLeft - (prev.offsetLeft + prev.width);
  } else {
    return 0;
  }
}

export const virtualizedHorizontalSortingStrategy: SortingStrategy = ({
  layoutRects,
  activeIndex,
  overIndex,
  index,
  activeNodeRect: fallbackActiveRect,
}): Transform | null => {
  const activeNodeRect = layoutRects[activeIndex] ?? fallbackActiveRect;

  if (!activeNodeRect) return null;

  const itemGap = getItemGap(layoutRects, index, activeIndex);

  // CASE 1: It's the active (dragged) item — move it to new position
  if (index === activeIndex) {
    const newIndexRect = layoutRects[overIndex];
    if (!newIndexRect) return null;

    let deltaX = newIndexRect.offsetLeft - activeNodeRect.offsetLeft;
    if (activeIndex < overIndex) {
      deltaX += newIndexRect.width - activeNodeRect.width;
    }

    return {
      x: deltaX,
      y: 0,
      ...defaultScale,
    };
  }

  // CASE 2: Items between original and new position shift left or right
  if (index > activeIndex && index <= overIndex) {
    return {
      x: -activeNodeRect.width - itemGap,
      y: 0,
      ...defaultScale,
    };
  }

  if (index < activeIndex && index >= overIndex) {
    return {
      x: activeNodeRect.width + itemGap,
      y: 0,
      ...defaultScale,
    };
  }

  // CASE 3: All others stay in place
  return {
    x: 0,
    y: 0,
    ...defaultScale,
  };
};
